import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common, query } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { GlobalDisclaimerLevel } from '@kurtosys/types/disclaimers/GlobalDisclaimerLevel.js';
import { observer } from 'mobx-react';
import * as Recharts from 'recharts';

import { TLabelFormatter } from '../../../models/charting/TLabelFormatter.js';
import { ITextProps } from '../../../models/index.js';
import { InjectedStyledComponent, ResponsiveComponentBase } from '../../../shared/index.js';
import { AccessibilityContext } from '../../base/Accessibility/Accessibility.js';
import { IAccessibilityContextValue } from '../../base/Accessibility/models/index.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import Disclaimers from '../../overview/Disclaimers/Disclaimers.js';

import DefaultLegendContent from './components/DefaultLegendContent/index.js';
import DefaultTooltipContent from './components/DefaultTooltipContent/DefaultTooltipContent.js';
import {
	IChartBaseConfiguration,
	IChartBaseProps,
	IChartBaseState,
	IMappedColor,
	ISingleChart,
} from './models/index.js';
import ChartSummary from './styledComponents/ChartSummary.js';
import DisclaimersWrapper from './styledComponents/DisclaimersWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CHART_BASE_CATEGORY_KEY, CHART_BASE_COMPONENT_KEY } from './ChartBase.meta.js';
import {
	applyAnimationLogic,
	applyCustomTickLabel,
	applyCustomTickValues,
	applyDataLength,
	applyFormatters,
	applyGlobalDisclaimers,
	applyLabelQueries,
	applyRebaseToComposedChartProps,
	applySeriesLabels,
	applyTickPivotPoint,
	applyTranslations,
	applyTranslationsToComposedChartProps,
	calculateBarChartHeight,
	getChartSyncTicks,
	getDynamicReferenceLines,
	validateLegendElement,
} from './ChartBaseFunctions.js';

/**
 * A base component for creating charts using Recharts library.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.className - The class name for the component.
 * @param {Array} props.breakpoints - An array of breakpoints for the chart.
 * @returns {JSX.Element} A React component that renders a chart.
 */
export class ChartBase extends ResponsiveComponentBase<IChartBaseProps, IChartBaseState, IChartBaseConfiguration> {
	categoryKey = CHART_BASE_CATEGORY_KEY;
	componentKey = CHART_BASE_COMPONENT_KEY;

	constructor(props: IChartBaseProps) {
		super(props);
	}

	/**
	 * Returns a LabelList element for rendering a label list in a chart.
	 * @param {Object} props - The props object containing the label list configuration.
	 * @param {string} props.dataKey - The data key to use for the label list.
	 * @param {Object} props.labelList - The configuration object for the label list.
	 * @returns {JSX.Element|undefined} A JSX element for rendering the label list, or undefined if no label list is configured.
	 */
	getLabelList(props: any): JSX.Element | undefined {
		const labelListProps = props && props.labelList;
		if (labelListProps) {
			let labelFormatter: TLabelFormatter | undefined;
			if (common.commonUtils.isNullOrUndefined(labelListProps.dataKey)) {
				labelListProps.dataKey = props.dataKey;
			}
			if (!common.commonUtils.isNullOrUndefined(labelListProps.formatter)) {
				labelFormatter = (label: string | number) => {
					return common.commonUtils.formatter(labelListProps.formatter as IFormatter, label);
				};
			}
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const LabelListElement = Recharts['LabelList'] as any;
			if (labelListProps.color) {
				return <LabelListElement {...labelListProps} formatter={labelFormatter} fill={labelListProps.color} />;
			}
			return <LabelListElement {...labelListProps} formatter={labelFormatter} />;
		}
		return undefined;
	}

	/**
	 * Returns a `Label` element for the chart.
	 * @param {Object} props - The props for the chart base component.
	 * @returns {JSX.Element} A `Label` element.
	 */
	getLabel(props: any) {
		const labelProps = props && props.label;
		if (labelProps) {
			const LabelElement = Recharts['Label'] as any;
			return <LabelElement {...labelProps} />;
		}
	}

	/**
	 * Returns an array of `Cell` elements for the chart.
	 * @param {Object} props - The props for the chart base component.
	 * @param {Array} [data=[]] - The data for the chart.
	 * @returns {Array} An array of `Cell` elements.
	 */
	getCells(props: any, data: any[] = []) {
		const colors = props && props.colors;
		if (!common.commonUtils.isNullOrEmpty(colors)) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const CellElement = Recharts['Cell'] as any;
			const cells = [];

			if (typeof colors[0] === 'string') {
				// Color Strings
				const count = props && props.count;
				for (let idx = 0; idx < count; idx++) {
					cells.push(<CellElement key={`cell-${idx}`} fill={colors[idx % colors.length]} />);
				}
			} else {
				// Mapped Colors
				data.forEach((dataItem: any, idx: number) => {
					const color = colors.find((c: IMappedColor) => c.key === dataItem.name);
					cells.push(<CellElement key={`cell-${idx}`} fill={color && color.color} />);
				});
			}

			return cells;
		}
	}

	/**
	 * Returns the props for the responsive container element.
	 * @param {IChartBaseProps} [props] - The props for the chart base component.
	 * @returns {Object} The props for the responsive container element.
	 */
	getResponsiveContainerProps(props?: IChartBaseProps) {
		if (props) {
			const { responsiveContainerProps = {}, composedChartProps = {}, elements = [] } = props;
			if (!responsiveContainerProps.forceHeight) {
				const barChartHeight = calculateBarChartHeight(composedChartProps, elements);
				if (barChartHeight) {
					responsiveContainerProps.height = barChartHeight;
				}
			}
			return responsiveContainerProps;
		}
		return {};
	}

	/**
	 * Returns the accessibility attributes for the chart container element.
	 * @param {string} id - The ID of the chart container element.
	 * @param {string} [summary] - The summary text for the chart.
	 * @param {string} [chartAccessibilitySummary] - The accessibility summary text for the chart.
	 * @returns {Object} The props for the chart container element.
	 */
	getChartContainerProps(id: string, summary?: string, chartAccessibilitySummary?: string) {
		if (summary) {
			return {
				'aria-labelledby': id,
			};
		}

		if (chartAccessibilitySummary && !summary) {
			return {
				'aria-label': chartAccessibilitySummary,
			};
		}

		return {
			'aria-hidden': true,
		};
	}

	/**
	 * A base component for rendering charts using Recharts library.
	 * @param {Object} props - The props object.
	 * @param {Object} props.composedChartProps - The props for the composed chart.
	 * @param {Array} props.elements - The array of chart elements.
	 * @param {Object} props.rebaseOptions - The options for rebasing the chart props.
	 * @param {string} props.chartBaseComponent - The base component for the chart.
	 * @param {string} props.appKey - The key for the app.
	 * @param {Object} props.queryStore - The query store object.
	 * @param {Object} props.disclaimersProps - The props for the disclaimers.
	 * @param {Array} props.cleanConfigurations - The array of clean configurations.
	 * @param {Object} translationContext - The translation context object.
	 * @param {Object} accessibilityContext - The accessibility context object.
	 * @returns {JSX.Element} The chart base component.
	 */
	renderChart = (
		props: IChartBaseProps | undefined,
		translationContext: any,
		accessibilityContext: IAccessibilityContextValue,
	) => {
		if (!props) {
			return null;
		}

		// Fix build issue. bad typings
		const responsiveContainerProps: any = this.getResponsiveContainerProps(props);
		const {
			composedChartProps = {},
			elements = [],
			rebaseOptions,
			chartBaseComponent = 'ComposedChart',
			appKey,
			queryStore,
		} = props;
		let { disclaimersProps } = props;

		applyTranslationsToComposedChartProps(composedChartProps, translationContext);
		applyRebaseToComposedChartProps(composedChartProps, rebaseOptions);

		const { cleanConfigurations } = props;
		let dataLength: number | undefined;
		let data = composedChartProps && composedChartProps.data;
		elements && elements.push(...getDynamicReferenceLines(elements, composedChartProps, cleanConfigurations));
		let chartProps: ISingleChart<any> = {};
		let combinedTicks: number[] = [];
		let combinedTickCount = 0;
		let syncTicks: { ticks: any[]; tickCount: number } | undefined;
		let chartSummary = undefined;
		if (cleanConfigurations) {
			if (cleanConfigurations.length > 0) {
				chartProps = cleanConfigurations[cleanConfigurations.length - 1];
				// Set up the data with the rebased composed chart props data
				data = data || chartProps.data;
				dataLength = data && data.length;
				chartSummary = cleanConfigurations[1] && cleanConfigurations[1].summary;
			}

			syncTicks = getChartSyncTicks(chartProps, elements, composedChartProps);
			// Used to allow the x and y axis to have the same ticks
			if (syncTicks) {
				combinedTicks = syncTicks.ticks;
				combinedTickCount = syncTicks.tickCount;
			}
		}

		let hasSuperscriptDisclaimer = false;
		// appKey is required so that we don't get all disclaimers returning when they are not setup
		if (appKey) {
			hasSuperscriptDisclaimer = applyGlobalDisclaimers(elements, data, queryStore, appKey);
			query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
			const globalDisclaimerStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
			if (globalDisclaimerStore) {
				const disclaimers = [];
				// gets all disclaimers to show at the bottom of the chart
				disclaimers.push(...(globalDisclaimerStore.get(GlobalDisclaimerLevel.app, appKey) as any[]));
				if (disclaimers && disclaimers.length > 0) {
					disclaimersProps = disclaimersProps || {};
					disclaimersProps.disclaimers = disclaimersProps.disclaimers || [];
					disclaimersProps.disclaimers.push(...disclaimers);
				}
			}
		}

		const ChartBaseComponent = Recharts[chartBaseComponent] as any;
		const chartAccessibilitySummary = accessibilityContext.accessibilityText('charting.summary', '');
		const summaryId = chartSummary ? `chart-summary-${common.commonUtils.generateGuid()}` : '';
		return (
			<Fragment>
				<Fragment>
					{chartSummary && <ChartSummary id={summaryId} title={chartSummary} level={2} />}
					<div {...this.getChartContainerProps(summaryId, chartSummary, chartAccessibilitySummary)}>
						<Recharts.ResponsiveContainer {...responsiveContainerProps} minHeight="600px">
							<ChartBaseComponent {...composedChartProps}>
								{elements.map((element, elementIndex) => {
									// reset element.props.length to data length
									applyDataLength(element, dataLength);
									applyTranslations(element, translationContext);
									// if not using equal axis ticks, fall back to original logic
									if (
										syncTicks &&
										(element.type === 'XAxis' || element.type === 'YAxis') &&
										element.props
									) {
										element.props.ticks = combinedTicks;
										element.props.tickCount = combinedTickCount;
									} else {
										applyTickPivotPoint(element, elements, composedChartProps);
									}
									applyLabelQueries(element, translationContext, queryStore);
									applyFormatters(element, translationContext, queryStore);
									applyCustomTickLabel(element);
									applySeriesLabels(element, translationContext);
									applyCustomTickValues(element, props);
									applyAnimationLogic(element, props);
									validateLegendElement(element, data);
									if (element.type === 'Legend' && element.props) {
										element.props.content = <DefaultLegendContent {...element.props} />;
									}
									if (element.type === 'Tooltip' && element.props) {
										element.props.content = <DefaultTooltipContent {...element.props} />;
									}
									const { type, props: elementProps = {} } = element;

									// Recharts Text element does not work so we need to implement a custom one
									if (type === 'Text') {
										const { label, scaleToFit, verticalAnchor, lineHeight, ...textProps } =
											elementProps as ITextProps;
										return (
											<text key={elementIndex} {...textProps}>
												{(elementProps as ITextProps).label}
											</text>
										);
									}
									const RechartsElement = Recharts[type] as any;
									return (
										<RechartsElement key={elementIndex} {...elementProps}>
											{this.getLabelList(elementProps)}
											{type === 'ReferenceLine' && this.getLabel(elementProps)}
											{type === 'Bar' &&
												this.getCells(
													elementProps,
													composedChartProps && composedChartProps.data,
												)}
										</RechartsElement>
									);
								})}
							</ChartBaseComponent>
						</Recharts.ResponsiveContainer>
					</div>
				</Fragment>
				{disclaimersProps && (
					<DisclaimersWrapper>
						<Disclaimers {...disclaimersProps} />
					</DisclaimersWrapper>
				)}
			</Fragment>
		);
	};

	render() {
		const { className } = this.props;
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				<TranslationContext.Consumer>
					{(translationContext) => {
						return (
							<AccessibilityContext.Consumer>
								{(accessibilityContext) => {
									return breakpoints.map((breakpoint, breakpointIndex) => {
										const { props } = breakpoint;
										const breakpointProps = this.getBreakpointComponentProps(
											breakpoint,
											breakpointIndex,
										);
										return (
											<Breakpoint {...breakpointProps}>
												<Wrapper className={className}>
													{this.renderChart(props, translationContext, accessibilityContext)}
												</Wrapper>
											</Breakpoint>
										);
									});
								}}
							</AccessibilityContext.Consumer>
						);
					}}
				</TranslationContext.Consumer>
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(ChartBase), CHART_BASE_CATEGORY_KEY, CHART_BASE_COMPONENT_KEY);
